<template>
  <div>
    <div class="segment-ct">
      <form-section
        v-if="displayTargetApplications && !isWorkflowCampaign"
        :title="$t('CampaignForm_Segment_SelectApplications_Title')"
        :description="$t('CampaignForm_Segment_SelectApplications_Desc')"
        :separator="true"
        :is-changed="model.diff && model.diff.includes('targetUsers')"
      >
        <form-item prop="target_application_enabled">
          <radio-group v-model="model.target_application_enabled" inline>
            <radio
              :border="true"
              :label="0"
              :title="$t('CampaignForm_Segment_Applications_All_Title')"
              :description="$t('CampaignForm_Segment_Applications_All_Desc')"
            />
            <radio
              :border="true"
              :label="1"
              :title="$t('CampaignForm_Segment_Applications_Target_Title')"
              :description="$t('CampaignForm_Segment_Applications_Target_Desc')"
            />
          </radio-group>
        </form-item>
        <template v-if="model.target_application_enabled">
          <div class="divider"></div>
          <form-item
            :label="$t('CampaignForm_Segment_Applications_Target_Title')"
          >
            <el-row>
              <el-col :span="8" class="p-right-17">
                <el-select
                  v-model="model.target_applications"
                  multiple
                  filterable
                  remote
                  :remote-method="fetchAppsRemote"
                  :loading="loading"
                  @focus="fetchAppsRemote()"
                >
                  <el-option
                    v-for="item in listOptions"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  >
                  </el-option>
                </el-select>
              </el-col>
            </el-row>
          </form-item>
        </template>
      </form-section>
      <form-section
        :title="$t('CampaignForm_Segment_Section_Specify_Title')"
        :description="$t('CampaignForm_Segment_Section_Specify_Description')"
        class="segments-form-section"
        :separator="true"
        :is-changed="model.diff && model.diff.includes('broadcast')"
      >
        <rest-form-item prop="broadcast">
          <radio-group v-model="model.broadcast" inline>
            <radio
              :border="true"
              :label="broadcastOptions.CUSTOM_SEGMENT"
              :title="$t('CampaignForm_Segment_Custom_Segment_Option')"
              :description="
                $t('CampaignForm_Segment_Custom_Segment_Option_Description')
              "
              data-qa="segment-custom-option-radio"
            />
            <radio
              v-if="rollingSegmentsAvailable"
              :border="true"
              :label="broadcastOptions.ROLLING_SEGMENT"
              :title="$t('CampaignForm_Segments_RollingSegment_Option')"
              :description="
                $t('CampaignForm_Segments_RollingSegment_Option_Description')
              "
              data-qa="segment-rolling-option-radio"
            />
            <radio
              :disabled="isBroadcastDisabled"
              :border="true"
              :label="broadcastOptions.EVERYONE_SEGMENT"
              :title="$t('CampaignForm_Segment_Everyone_Segment_Option')"
              :description="
                $t('CampaignForm_Segment_Everyone_Segment_Option_Description')
              "
              data-qa="segment-everyone-option-radio"
            />
          </radio-group>
        </rest-form-item>
        <div v-if="model.broadcast === broadcastOptions.CUSTOM_SEGMENT">
          <conditions-editor
            ref="conditions-editor"
            v-model="conditions"
            data-model-name="Campaign"
            data-model-attribute="conditions"
            attribute-list-data-url="/segment/segment/attribute-list-data"
            :preview="!$can('createProjectItem') || !$can('updateProjectItem')"
            :no-data-alert="false"
            :single-level="false"
          />
        </div>
        <div v-if="model.broadcast === broadcastOptions.ROLLING_SEGMENT">
          <rolling-segment v-model="model.rolling_segment_id" />
        </div>
      </form-section>
      <form-section-checkbox
        v-if="showSeedList"
        :title="$t('CampaignForm_Segment_SendToSeedList_Title')"
        :description="$t('CampaignForm_Segment_SendToSeedList_Desc')"
        v-model="model.seed_list_enabled"
        :show-expander="false"
        :is-changed="model.diff && model.diff.includes('seedList')"
      >
      </form-section-checkbox>
      <form-section
        :title="$t('CampaignForm_Segment_TargetTriggeredDevices_Title')"
        v-if="displayTargetTriggeredDevices"
        :is-changed="model.diff && model.diff.includes('targetUsers')"
      >
        <form-item prop="target_devices">
          <el-row>
            <el-col :span="24">
              <target-triggered-devices
                style="margin-top: 8px;"
                :model="model"
              />
            </el-col>
          </el-row>
        </form-item>
      </form-section>
      <form-section
        :title="$t('CampaignForm_Segment_Target_Devices_Section')"
        v-if="displayTargetDevices"
        :is-changed="
          model.diff && model.diff.includes('target_authorised_devices')
        "
      >
        <el-row>
          <el-col :span="24">
            <target-devices style="margin-top: 8px;" :model="model" />
          </el-col>
        </el-row>
      </form-section>
      <form-section
        :title="$t('CampaignForm_Segment_Target_Group_Section')"
        v-if="activeChannels.length > 0 && !isWorkflowCampaign"
        :is-changed="model.diff && model.diff.includes('target')"
      >
        <el-tabs v-model="activeTab" :stretch="false">
          <el-tab-pane
            :label="$t(`CampaignForm_Segment_Target_${channel}`)"
            :name="channel"
            :key="channel"
            v-for="channel in activeChannels"
          >
            <target-group
              :model="model"
              :target-key="`${channel}_addressable`"
            />
          </el-tab-pane>
        </el-tabs>
      </form-section>
      <form-section
        v-if="
          (environmentAvailable || model.web_push || encryptionEnabled) &&
            !isWorkflowCampaign
        "
        :title="$t('CampaignForm_Segment_Restrict_Delivery_Section')"
      >
        <form-section
          v-if="environmentAvailable"
          :title="$t('CampaignForm_Segment_PushEnvironments')"
          :description="$t('CampaignForm_Segment_PushEnvironments_Description')"
          :separator="true"
          :is-changed="model.diff && model.diff.includes('environments')"
        >
          <el-row>
            <el-col :span="24">
              <el-tabs v-model="activeEnvironmentTab" :stretch="false">
                <el-tab-pane
                  :label="$t(`CampaignForm_Segment_Environment_${channel}`)"
                  :name="channel"
                  :key="channel"
                  v-for="channel in environmentChannels"
                >
                  <choose-push-environments
                    :model="model"
                    :with-android-prod="
                      !!(channel === 'android_push' && hasAndroidProductionEnv)
                    "
                    :with-android-legacy="
                      !!(channel === 'android_push' && hasAndroidLegacyEnv)
                    "
                    :with-ios-prod="
                      !!(channel === 'ios_push' && hasIosProductionEnv)
                    "
                    :with-ios-sandbox="
                      !!(channel === 'ios_push' && hasIosSandboxEnv)
                    "
                    :with-ios-legacy="
                      !!(channel === 'ios_push' && hasIosLegacyEnv)
                    "
                  />
                </el-tab-pane>
              </el-tabs>
            </el-col>
          </el-row>
        </form-section>
        <form-section
          v-if="model.web_push && !isWorkflowCampaign"
          :title="$t('CampaignForm_Setup_ChooseBrowsers')"
          :description="$t('CampaignForm_Setup_ChooseBrowsers_Description')"
          :separator="true"
          :is-changed="model.diff && model.diff.includes('webPush')"
        >
          <el-row>
            <el-col :span="24">
              <choose-browsers style="margin-top: 8px;" :model="model" />
            </el-col>
          </el-row>
        </form-section>
        <form-section
          v-if="encryptionEnabled"
          :title="$t('CampaignForm_Setup_Encryption_Title')"
          :description="$t('CampaignForm_Setup_Encryption_Description')"
          :separator="true"
          :is-changed="model.diff && model.diff.includes('encryption')"
        >
          <el-row>
            <el-col :span="24">
              <rest-form-item prop="encryption">
                <message-encryption :model="model" />
              </rest-form-item>
            </el-col>
          </el-row>
        </form-section>
      </form-section>
    </div>
    <div class="footer">
      <div class="footer-ct">
        <el-button type="primary" @click="nextStep" data-qa="next-step-btn">
          {{ nextBtnLabel }}
          <i class="el-icon-arrow-right el-icon-right"></i>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import ConditionsEditor from '@/common/components/ConditionsEditor'
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import RestFormItem from '@/common/components/rest/RestFormItem'
import { EventBus } from '@/core/EventBus'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import TargetGroup from '@/modules/campaign-manage/components/segment/TargetGroup'
import TargetDevices from '@/modules/campaign-manage/components/segment/TargetDevices'
import ChooseBrowsers from '@/modules/campaign-manage/components/segment/ChooseBrowsers'
import MessageEncryption from '@/modules/campaign-manage/components/segment/MessageEncryption'
import ChoosePushEnvironments from '@/modules/campaign-manage/components/segment/ChoosePushEnvironments'
import { $hasFeature } from '@/core/utils/VueAcl'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { cloneDeep } from 'lodash'
import FormSectionCheckbox from '@/common/components/form/FormSectionCheckbox'
import RollingSegment from '@/modules/campaign-manage/components/segment/RollingSegment'
import { broadcastOptions } from '@/modules/campaign-manage/enum/BroadcastOptions'
import TargetTriggeredDevices from '@/modules/campaign-manage/components/segment/TargetTriggeredDevices.vue'

export default {
  name: 'StepSegment',
  components: {
    TargetTriggeredDevices,
    FormSectionCheckbox,
    TargetGroup,
    TargetDevices,
    ChooseBrowsers,
    RestFormItem,
    FormItem,
    ConditionsEditor,
    Radio,
    RadioGroup,
    FormSection,
    MessageEncryption,
    ChoosePushEnvironments,
    RollingSegment
  },
  props: {
    model: {
      type: Object
    }
  },
  created() {
    this.fetchAppsRemote()
    this.activeTab = this.activeChannels[0]
    this.activeEnvironmentTab = this.environmentChannels[0]
    this.currentRollingSegment = this.model.rolling_segment_id
  },
  data() {
    return {
      campaignTypeEnum,
      broadcastOptions,
      loading: false,
      listOptions: [],
      targetChannels: ['sms', 'email', 'inbox', 'webhook'],
      activeTab: null,
      activeEnvironmentTab: null,
      currentRollingSegment: null
    }
  },

  methods: {
    fetchAppsRemote(queryString) {
      this.loading = true
      return RestApi(
        projectEndpoint({
          method: 'get',
          url: 'applications',
          params: { sort: 'name', name: queryString }
        })
      )
        .then(({ data }) => {
          this.listOptions = data.sort(
            (a, b) =>
              (this.model.target_applications || []).includes(b.id) -
              (this.model.target_applications || []).includes(a.id)
          )
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    nextStep() {
      EventBus.$emit('campaign-create-next-step')
    }
  },
  computed: {
    conditions: {
      get: function() {
        const conditions = cloneDeep(this.model.conditions)

        Object.keys(conditions).forEach(section => {
          Object.keys(conditions[section]).forEach(row => {
            if (
              conditions[section][row][4] &&
              conditions[section][row][4][0] === 'date' &&
              ['between', 'between_time'].includes(
                conditions[section][row][4][1]
              ) &&
              conditions[section][row][4][2] &&
              typeof conditions[section][row][4][2] === 'string'
            ) {
              // eslint-disable-next-line standard/computed-property-even-spacing
              conditions[section][row][4][2] = conditions[section][
                row
              ][4][2].split(' to ')
            }
          })
        })

        return conditions
      },
      set: function(newVal) {
        this.model.conditions = newVal
      }
    },
    nextBtnLabel() {
      let index = 4
      if (
        this.model.trigger === campaignTriggerTypes.TRIGGER_LOCATION ||
        this.model.trigger === campaignTriggerTypes.TRIGGER_EVENT
      ) {
        index = 5
      }

      if (
        (this.$isRole('admin') ||
          this.$isRole('admin-support') ||
          this.$hasFeature('campaignContentPreview')) &&
        this.model.type === campaignTypeEnum.TYPE_NOTIFICATION &&
        !!this.model.email
      ) {
        return this.$t('CampaignForm_Segment_NextButtonPreview_Title', {
          index: index
        })
      }

      return this.$t('CampaignForm_Segment_NextButton_Title', { index: index })
    },
    activeChannels() {
      return this.targetChannels.filter(channel => {
        return this.model[channel] === 1
      })
    },
    environmentAvailable() {
      return (
        (this.iosEnabled || this.androidEnabled) &&
        (this.hasAndroidProductionEnv ||
          this.hasAndroidLegacyEnv ||
          this.hasIosProductionEnv ||
          this.hasIosSandboxEnv ||
          this.hasIosLegacyEnv)
      )
    },
    isWorkflowCampaign() {
      return this.model.type === campaignTypeEnum.TYPE_WORKFLOW
    },
    androidEnabled() {
      return this.environmentChannels.includes('android_push')
    },
    iosEnabled() {
      return this.environmentChannels.includes('ios_push')
    },
    hasAndroidProductionEnv() {
      return (
        $hasFeature('androidPushProduction') ||
        $hasFeature('androidPushProductionV1') ||
        this.model.android_push_production
      )
    },
    hasAndroidLegacyEnv() {
      return $hasFeature('androidPushLegacy') || this.model.android_push_legacy
    },
    hasIosProductionEnv() {
      return $hasFeature('iosPushProduction') || this.model.ios_push_production
    },
    hasIosSandboxEnv() {
      return $hasFeature('iosPushSandbox') || this.model.ios_push_sandbox
    },
    hasIosLegacyEnv() {
      return $hasFeature('iosPushLegacy') || this.model.ios_push_legacy
    },
    environmentChannels() {
      const availableChannels = ['android_push', 'ios_push']

      return availableChannels.filter(channel => {
        return this.model[channel] === 1
      })
    },
    encryptionEnabled() {
      if (
        this.$store.state.app.project.push_encryption &&
        this.model.type !== campaignTypeEnum.TYPE_WORKFLOW
      ) {
        return this.model.ios_push === 1 || this.model.android_push === 1
      } else {
        return false
      }
    },
    displayTargetApplications() {
      return (
        $hasFeature('targetApplicationEnabled') ||
        (this.model.target_applications &&
          Array.isArray(this.model.target_applications) &&
          this.model.target_applications.length > 0)
      )
    },
    displayTargetDevices() {
      return (
        ($hasFeature('allowAuthorisedDevices') && this.deviceChannelIsActive) ||
        (this.model.id && this.model.target_authorised_devices === 1)
      )
    },
    displayTargetTriggeredDevices() {
      return (
        ($hasFeature('targetDevicesEnabled') &&
          this.deviceChannelIsActive &&
          this.model.type === campaignTypeEnum.TYPE_NOTIFICATION &&
          this.model.trigger === campaignTriggerTypes.TRIGGER_EVENT) ||
        (this.model.id && this.model.target_devices)
      )
    },
    deviceChannelIsActive() {
      return (
        this.model.ios_push === 1 ||
        this.model.android_push === 1 ||
        this.model.web_push === 1 ||
        this.model.inapp === 1 ||
        this.model.onsite === 1 ||
        (this.model.inbox === 1 && !$hasFeature('inboxProfileOn'))
      )
    },
    showSeedList() {
      return $hasFeature('featureSeedList') && $hasFeature('seedListEnabled')
    },
    isBroadcastDisabled() {
      return (
        $hasFeature('broadcastDisabled') ||
        (!$hasFeature('allowBroadcastForMultistageTimebasedCampaigns') &&
          this.model.type === campaignTypeEnum.TYPE_WORKFLOW &&
          this.model.trigger === campaignTriggerTypes.TRIGGER_NONE)
      )
    },
    rollingSegmentsAvailable() {
      return $hasFeature('rollingSegments')
    }
  },

  watch: {
    'model.broadcast': function(newValue) {
      if (newValue !== broadcastOptions.ROLLING_SEGMENT) {
        if (this.model.rolling_segment_id !== null) {
          this.currentRollingSegment = this.model.rolling_segment_id
        }

        this.model.rolling_segment_id = null
      }

      if (
        this.currentRollingSegment &&
        newValue === broadcastOptions.ROLLING_SEGMENT
      ) {
        this.model.rolling_segment_id = this.currentRollingSegment
      }
    }
  }
}
</script>

<style scoped>
>>> .el-radio {
  min-height: 68px !important;
  max-height: 80px !important;
  white-space: unset;
}

>>> .title {
  font-weight: 600;
  line-height: 22px;
  color: #5c6374;
}

>>> .el-tabs__header {
  margin-bottom: 23px;
}

.row {
  width: 100%;
}

.divider {
  margin: 32px -20px;
  border-top: 1px solid #ebeef5;
}

.title {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #4b515f;
}

.segments-form-section {
  overflow: visible;
}
.segment-ct {
  padding-bottom: 60px;
}
.footer {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}
.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/pages/StepSegment.vue